import React from "react";

import WComponent from "../common/WComponent";
import ToolsReviewSection from "../clients/Tools/kit/ToolsReviewSection";
import ToolsReviewItem, { ToolsReviewResolution } from "../clients/Tools/kit/ToolsReviewItem";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import UtilityService from "../../services/UtilityService";
import AgentFindingSteps from "./AgentFindingSteps";
import AgentReviewItemTools from "./AgentReviewItemTools";
import AgentWorkflowAction from "./AgentWorkflowAction";
import AgentJournalBooking from "./AgentJournalBooking";

import "./agent-run-report.css";

class AgentRunProposals extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			openReasonKeys: [],
		};
	}

	onToggleReason = ({ proposalKey }) => {
		const nextKeys = UtilityService.toggleInList({ list: this.state.openReasonKeys, item: proposalKey });
		this.update({ openReasonKeys: nextKeys });
	};

	proposalsOfRun() {
		const { run, review } = this.props;
		const allProposals = review ? review.proposals : run.proposals;
		const split = PlaybookReviewService.splitProposalsByKind({ proposals: allProposals });
		return split;
	}

	renderProposalLine = (line, index) => {
		return (
			<div key={index} className="agent-run-report__journal-line">
				<span className="agent-run-report__journal-account">{line.accountName}</span>
				<span className="agent-run-report__journal-tax">{line.taxCode || ""}</span>
				<span className="agent-run-report__journal-amount tk-amount">{line.side === "debit" ? UtilityService.formatCentsAsCurrency({ cents: line.amountCents }) : ""}</span>
				<span className="agent-run-report__journal-amount tk-amount">{line.side === "credit" ? UtilityService.formatCentsAsCurrency({ cents: line.amountCents }) : ""}</span>
			</div>
		);
	};

	renderProposal = proposal => {
		const { run, review, busySubjectKey, openBooksProposalId, onDecide, onBookJournal, onRemoveJournal, onOpenBooksPanel } = this.props;
		const { openReasonKeys } = this.state;
		const { payload } = proposal;
		const proposalKey = `proposal-${proposal.id}`;
		const isRejected = proposal.decision && proposal.decision.decision === "reject";
		const isReasonOpen = openReasonKeys.includes(proposalKey);
		const firstLine = proposal.lines.length > 0 ? proposal.lines[0] : null;
		const firstLineAccountId = firstLine ? firstLine.qboAccountId : null;
		const showMe = firstLineAccountId
			? () => onOpenBooksPanel({ proposalId: proposal.id, qboAccountId: firstLineAccountId, tab: "ledger", title: firstLine.accountName })
			: null;

		return (
			<ToolsReviewItem
				key={proposalKey}
				id={proposalKey}
				status={isRejected ? "na" : "info"}
				label={payload.memo}
				detail={`Dated ${payload.txnDate} · $${UtilityService.formatCurrency(payload.total)} · ${proposal.confidence} confidence`}
			>
				<div className="agent-run-report__journal-lines">
					<div className="agent-run-report__journal-line agent-run-report__journal-line--head">
						<span>Account</span>
						<span>Tax</span>
						<span>Debit</span>
						<span>Credit</span>
					</div>
					{proposal.lines.map(this.renderProposalLine)}
				</div>
				<ToolsReviewResolution text="Accepting records that the entry is right. Booking it in QuickBooks is a separate step, checked against QuickBooks first." kindLabel="Journal entry">
					<AgentFindingSteps
						subject={proposal}
						isProposal
						review={review}
						isBusy={busySubjectKey === proposalKey}
						onDecide={choice => onDecide({ proposalId: proposal.id, ...choice })}
						onConfirmStillApplies={null}
					/>
					{review && <AgentJournalBooking runId={run.id} proposal={proposal} isBusy={busySubjectKey === proposalKey} onBook={onBookJournal} onRemove={onRemoveJournal} />}
				</ToolsReviewResolution>
				{isReasonOpen && (
					<div className="agent-run-report__aside">
						<span className="agent-run-report__aside-label">Why</span>
						{proposal.reason}
					</div>
				)}
				<AgentReviewItemTools
					isEvidenceOpen={isReasonOpen}
					onToggleEvidence={() => this.onToggleReason({ proposalKey })}
					hasLastMonth={false}
					isShowMeOpen={openBooksProposalId === proposal.id}
					onShowMe={showMe}
				/>
			</ToolsReviewItem>
		);
	};

	renderJournals() {
		const { collapsedAreas, onToggleArea } = this.props;
		const { journals } = this.proposalsOfRun();
		if (journals.length === 0) return null;
		const decidedCount = journals.filter(proposal => !!proposal.decision).length;
		const sectionTitle = PlaybookReviewService.proposalsSectionTitle();
		const isExpanded = !(collapsedAreas || {})[sectionTitle];
		return (
			<ToolsReviewSection
				title={sectionTitle}
				meta="nothing is posted until someone books it"
				doneCount={decidedCount}
				totalCount={journals.length}
				isExpanded={isExpanded}
				onToggle={() => onToggleArea({ area: sectionTitle, isExpanded })}
			>
				{journals.map(this.renderProposal)}
			</ToolsReviewSection>
		);
	}

	renderWorkflowAction = proposal => {
		const { run, review, busySubjectKey, onExecuteAction, onRejectAction } = this.props;
		const canDecide = !!review && review.viewer.canDecide;
		return (
			<AgentWorkflowAction
				key={proposal.id}
				proposal={proposal}
				canDecide={canDecide}
				waitingText={PlaybookReviewService.whyWorkflowActionsWait({ run, review })}
				isBusy={busySubjectKey === `proposal-${proposal.id}`}
				onExecute={() => onExecuteAction({ proposalId: proposal.id })}
				onReject={choice => onRejectAction({ proposalId: proposal.id, ...choice })}
			/>
		);
	};

	renderWorkflowActions() {
		const { collapsedAreas, onToggleArea } = this.props;
		const { workflowActions } = this.proposalsOfRun();
		if (workflowActions.length === 0) return null;
		const settledCount = workflowActions.filter(proposal => PlaybookReviewService.isWorkflowActionSettled({ proposal })).length;
		const sectionTitle = PlaybookReviewService.workflowActionsSectionTitle();
		const isExpanded = !(collapsedAreas || {})[sectionTitle];
		return (
			<ToolsReviewSection
				title={sectionTitle}
				meta="nothing happens until someone executes it"
				doneCount={settledCount}
				totalCount={workflowActions.length}
				isExpanded={isExpanded}
				onToggle={() => onToggleArea({ area: sectionTitle, isExpanded })}
			>
				{workflowActions.map(this.renderWorkflowAction)}
			</ToolsReviewSection>
		);
	}

	render() {
		return (
			<React.Fragment>
				{this.renderWorkflowActions()}
				{this.renderJournals()}
			</React.Fragment>
		);
	}
}

export default AgentRunProposals;
